import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Home, HandCoins, Users, Building2, Globe, Coins, Factory, Percent, BellRing, Webhook, Mail, ScrollText, ReceiptText, Repeat, Inbox, Network, BarChart3, Combine, Blocks, Send, FileText, Receipt, CreditCard } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";

const NAV_ITEMS = [
  { label: "Clients", path: "/clients", icon: HandCoins },
  { label: "Organisations", path: "/organisations", icon: Building2 },
  { label: "Users", path: "/users", icon: Users },
  { label: "Invoices", path: "/invoices", icon: FileText },
  { label: "Bills", path: "/bills", icon: Receipt },
  { label: "Expenses", path: "/expenses", icon: CreditCard },
  { label: "Analytics", path: "/analytics", icon: BarChart3 },
  { label: "3-way Match", path: "/three-way-match", icon: Combine },
  { label: "Modules Audit Log", path: "/modules-audit-logs", icon: Blocks },
  { label: "Home", path: "/", icon: Home },
  { label: "Invoice Reminders", path: "/invoice-reminders", icon: BellRing },
  { label: "Recurring Invoices", path: "/recurring-invoices", icon: Repeat },
  { label: "Invoice Audit Logs", path: "/invoice-audit-logs", icon: ScrollText },
  { label: "Bill Audit Logs", path: "/bill-audit-logs", icon: ReceiptText },
  { label: "Webhook Deliveries", path: "/webhook-deliveries", icon: Webhook },
  { label: "Received E-Invoices", path: "/received-e-invoices", icon: Inbox },
  { label: "Sent E-Invoices", path: "/sent-e-invoices", icon: Send },
  { label: "E-Invoicing Registrations", path: "/e-invoicing-registrations", icon: Network },
  { label: "Gmail Connections", path: "/gmail-connections", icon: Mail },
  { label: "Countries", path: "/countries", icon: Globe },
  { label: "Currencies", path: "/currencies", icon: Coins },
  { label: "Industries", path: "/industries", icon: Factory },
  { label: "Country Tax Table", path: "/country-tax-rates", icon: Percent },
] as const;

interface CommandMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandMenu({ open, onOpenChange }: CommandMenuProps) {
  const navigate = useNavigate();

  const navigateTo = useCallback(
    (path: string) => {
      onOpenChange(false);
      navigate(path);
    },
    [navigate, onOpenChange],
  );

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Cmd+K / Ctrl+K to toggle
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
        return;
      }

      // Number keys 1-N while open
      if (open) {
        const num = parseInt(e.key, 10);
        if (num >= 1 && num <= NAV_ITEMS.length) {
          e.preventDefault();
          navigateTo(NAV_ITEMS[num - 1]!.path);
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange, navigateTo]);

  return (
    <CommandDialog
      className="sm:max-w-3xl"
      open={open}
      onOpenChange={onOpenChange}
      title="Navigation"
      description="Navigate to a page"
    >
      <CommandInput placeholder="Search pages..." />
      <CommandList className="max-h-[min(65vh,640px)]">
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Pages">
          {NAV_ITEMS.map((item, i) => (
            <CommandItem
              key={item.path}
              onSelect={() => navigateTo(item.path)}
            >
              <item.icon />
              {item.label}
              <CommandShortcut>{i + 1}</CommandShortcut>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
